<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: RelationshipTree                                      |
| Section: views/trees                                             |
| Purpose: Route visualizing relationships between selected people.|
+------------------------------------------------------------------+
-->
<template>
    <div class="RelationshipTree_wrapper">
        <div class="wrapper">
            <h2 ref="inlineEditorRoot" class="relationship-title">
                <template v-for="part in relationshipTitleParts" :key="part.key">
                    <span v-if="part.type === 'text'">{{ part.text }}</span>
                    <span v-else class="inline-person-editor">
                        <button v-if="activeInlineEditor !== part.personNumber" type="button"
                            class="inline-person-trigger" @click.stop="startInlineEdit(part.personNumber)">
                            <span>{{ getTitlePersonName(part.personNumber) }}</span>
                            <i class="fas fa-caret-down inline-person-caret" aria-hidden="true"></i>
                        </button>
                        <span v-else class="inline-editor-popover" @click.stop>
                            <input :ref="`inlineInput${part.personNumber}`" :value="getPersonSearchQuery(part.personNumber)"
                                type="text" autocomplete="off" :placeholder="getPersonSearchPlaceholder(part.personNumber)"
                                @input="handlePersonSearchInput(part.personNumber, $event)"
                                @keyup.enter="handlePickerEnter(part.personNumber)" @keydown.esc="cancelInlineEdit" />
                            <ul v-if="shouldShowSuggestions(part.personNumber)" class="search-suggestions" role="listbox">
                                <li v-for="suggestion in getPersonSuggestions(part.personNumber)"
                                    :key="`p${part.personNumber}-${suggestion.personID}`" role="option"
                                    @click="selectSuggestedPerson(part.personNumber, suggestion, $event)"
                                    @auxclick="handleSuggestionAuxClick(part.personNumber, suggestion, $event)"
                                    @keydown.enter.prevent="selectSuggestedPerson(part.personNumber, suggestion, $event)"
                                    @keydown.space.prevent="selectSuggestedPerson(part.personNumber, suggestion, $event)"
                                    tabindex="0"
                                    :aria-label="$t('relationshipTree.selectPersonAria', { name: suggestionLabel(suggestion) })">
                                    <span v-if="shouldHide(suggestion)" class="locked_name">
                                        <img src="@/assets/icons/lock_black.svg" alt="lock" />
                                        {{ $t('nav.privacyHidden') }}
                                    </span>
                                    <span v-else>
                                        {{ suggestionLabel(suggestion) }}
                                    </span>
                                    <!-- <br> -->
                                    <span v-if="shouldHide(suggestion)"></span>
                                    <span v-else>{{ formatYears(suggestion.birthdatetr, suggestion.altbirthdatetr,
                                        suggestion.deathdatetr, suggestion.burialdatetr, suggestion.living) }}</span>
                                </li>
                            </ul>
                        </span>
                    </span>
                </template>
            </h2>

            <p class="relationship-subtitle">
                {{ $t('relationshipTree.clickNameToChange') }}
            </p>

            <p v-if="isSamePersonSelected" class="picker-hint picker-hint--error">
                {{ $t('relationshipTree.chooseTwoPeople') }}
            </p>
            <p v-else-if="pickerHint" class="picker-hint">{{ pickerHint }}</p>
            <p v-if="pickerError" class="picker-hint picker-hint--error">{{ pickerError }}</p>

            <!-- Loader -->
            <LoadingSpinner v-if="loading" />

            <!-- Foutmelding -->
            <p class="error-message" v-if="error">
                <strong>{{ error }}</strong>
            </p>

            <!-- Als er een relatie gevonden is tonen we de complete chain -->
            <div v-if="!loading && relationshipFound" class="relationship-chain">

                <!-- Tabbladen voor alternatieve paden (meerdere gemeenschappelijke voorouders) -->
                <div v-if="allPaths.length > 1" class="path-tabs">
                    <button v-for="(path, index) in allPaths" :key="index" class="path-tab"
                        :class="{ 'path-tab--active': activePathIndex === index }" @click="switchPath(index)">
                        <template v-if="getPathLcaName(path)">
                            {{ $t('relationshipTree.viaPath', { name: getPathLcaName(path) }) }}
                        </template>
                        <template v-else>
                            {{ $t('relationshipTree.pathN', { n: index + 1 }) }}
                        </template>
                    </button>
                </div>

                <!-- <p class="relationship-summary">{{ relationshipSummary }}</p> -->

                <div v-if="graphLoading" class="graph-loading">{{ $t('relationshipTree.loadingGraph') }}</div>
                <p v-if="graphError" class="error-message"><strong>{{ graphError }}</strong></p>

                <div v-if="gridAvailable" class="relationship-graph">
                    <div v-if="!isDirectLine" class="graph-legend">
                        <span class="legend-item legend-item--left"></span>
                        <span class="legend-item legend-item--center">{{ $t('relationshipTree.commonAncestors')
                        }}</span>
                        <span class="legend-item legend-item--right"></span>
                    </div>
                    <p v-if="isAffinal" class="graph-affinal-note">{{ $t('relationshipTree.affinalRelationship') }}</p>

                    <div ref="relGridScroll" class="rel-grid-scroll">
                        <div class="rel-grid-fit" :style="relGridFitStyle">
                            <div ref="relGridContainer" class="rel-grid-container" :style="relGridContainerStyle">
                                <!-- SVG connector lines, drawn below person cards -->
                                <svg class="rel-grid-svg" :width="relGridWidth" :height="relGridHeight"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path v-for="(edge, idx) in gridLayout.edges" :key="'e' + idx"
                                        :d="relEdgePath(edge)" fill="none" stroke="rgba(255,255,255,0.28)"
                                        stroke-width="2" />
                                    <path v-for="(line, idx) in gridLayout.siblingLines" :key="'sl' + idx"
                                        :d="relSiblingLinePath(line)" fill="none" stroke="rgba(255,255,255,0.28)"
                                        stroke-width="2" />
                                </svg>

                                <!-- Person cards + spacer labels positioned on CSS grid -->
                                <div class="rel-grid" :style="relGridStyle">
                                    <!-- Spacer label cells -->
                                    <div v-for="(spacer, idx) in gridLayout.spacers" :key="'s' + idx"
                                        class="rel-grid-spacer" :style="relCellPositionStyle(spacer)">
                                        <span class="rel-grid-spacer-label">{{ spacer.label }}</span>
                                    </div>

                                    <div v-for="(cell, idx) in gridLayout.cells" :key="'c' + idx" class="rel-grid-cell"
                                        :style="relCellPositionStyle(cell)">
                                        <!-- LCA pair: two persons share one cell -->
                                        <div v-if="cell.lcaPair" class="rel-lca-pair">
                                            <PersonCard :isSmall="true" :photoSrc="cell.person.photoPath"
                                                :name="displayName(cell.person)"
                                                :years="formatYears(cell.person.birthdatetr, null, cell.person.deathdatetr, null, cell.person.living)"
                                                :gender="cell.person.sex || 'M'"
                                                :href="`/person/${cell.person.personID}`"
                                                :personID="cell.person.personID" :firstname="cell.person.firstname" :lastname="cell.person.lastname"
                                                :living="cell.person.living"
                                                :placeholder="cell.person.placeholder"
                                                :class="{ 'PersonCard_small_guest': shouldHide(cell.person) }" />
                                            <PersonCard :isSmall="true" :photoSrc="cell.lcaPair.photoPath"
                                                :name="displayName(cell.lcaPair)"
                                                :years="formatYears(cell.lcaPair.birthdatetr, null, cell.lcaPair.deathdatetr, null, cell.lcaPair.living)"
                                                :gender="cell.lcaPair.sex || 'M'"
                                                :href="`/person/${cell.lcaPair.personID}`"
                                                :personID="cell.lcaPair.personID" :firstname="cell.lcaPair.firstname" :lastname="cell.lcaPair.lastname"
                                                :living="cell.lcaPair.living"
                                                :placeholder="cell.lcaPair.placeholder"
                                                :class="{ 'PersonCard_small_guest': shouldHide(cell.lcaPair) }" />
                                        </div>
                                        <!-- Single person -->
                                        <PersonCard v-else :isSmall="true" :photoSrc="cell.person.photoPath"
                                            :name="displayName(cell.person)"
                                            :years="formatYears(cell.person.birthdatetr, null, cell.person.deathdatetr, null, cell.person.living)"
                                            :gender="cell.person.sex || 'M'" :href="`/person/${cell.person.personID}`"
                                            :personID="cell.person.personID" :firstname="cell.person.firstname" :lastname="cell.person.lastname"
                                            :living="cell.person.living"
                                            :placeholder="cell.person.placeholder"
                                            :class="{ 'PersonCard_small_guest': shouldHide(cell.person) }" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Toon samenvatting van de relatie -->
                <p class="relationship-description">{{ personName2 }} is de <span>{{ naturalRelationship }}</span> van
                    {{ personName1
                    }}
                </p>
            </div>

            <!-- Geen relatie gevonden -->
            <p class="error-message" v-if="!loading && relationshipFound === false && !error && hasSelectedPair">
                <strong>{{ $t('relationshipTree.noRelationshipFound', { maxDepth: maxRelationshipDepth }) }}</strong>
            </p>

        </div>

        <button v-if="gridAvailable && !graphLoading" type="button" class="rel-grid-download-btn"
            :disabled="isExportingTree" @click="downloadTreeImage">
            <i class="fas fa-download"></i>
            {{ isExportingTree ? $t('relationshipTree.exporting') : $t('relationshipTree.downloadImage') }}
        </button>

    </div>
</template>

<script>
import html2canvas from 'html2canvas';
import {
    getRelationship,
    getPersonById,
    getAncestors,
    getParents,
    getFamilyDetails,
    searchPeopleSuggestions,
} from '@/api/personService';
import { formatYears } from '@/utils/dateUtils.js';
import PersonCard from '@/atoms/PersonCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { computeDutchRelation } from '@/utils/computeDutchRelation';
import { fetchDnaTesters } from '@/api/apiService';
import { isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

// Grid layout constants (px)
const REL_CELL_W = 326;
const REL_SPACER_W = 100;
const REL_CELL_H = 100;
const REL_GAP_X = 40;
const REL_GAP_Y = 40;
const RELATIONSHIP_DEPTH = 25;
const DEFAULT_RELATIONSHIP_ROOT = 'I43';

export default {
    name: 'RelationshipTree',
    components: {
        PersonCard,
        LoadingSpinner,
    },
    setup() {
        // Injecteer de auth-token, gebruikersnaam en logout-functie vanuit de App.vue
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    data() {
        return {
            loading: false,
            relationshipFound: null,
            allPaths: [],          // Alle gevonden paden (één per gemeenschappelijke voorouder)
            activePathIndex: 0,    // Huidig geselecteerd tabblad
            relationshipSummary: '',
            error: null,
            person1: null,
            person2: null,
            graphLoading: false,
            graphError: null,
            graphData: null,
            relGridFitScale: 1,
            isExportingTree: false,
            selectedPerson1Id: null,
            selectedPerson2Id: null,
            selectedPerson1Label: '',
            selectedPerson2Label: '',
            personSearchQuery1: '',
            personSearchQuery2: '',
            personSuggestions1: [],
            personSuggestions2: [],
            activeInlineEditor: null,
            searchDebounceTimer1: null,
            searchDebounceTimer2: null,
            pickerError: null,
            maxRelationshipDepth: RELATIONSHIP_DEPTH,
            ignoreNextRouteSync: false,
            dnaTesters: {},
        };
    },
    computed: {
        personName1() {
            if (this.person1) return `${this.person1.firstname} ${this.person1.lastname}`.trim();
            return this.selectedPerson1Label || this.$t('relationshipTree.personAFallback');
        },
        personName2() {
            if (this.person2) return `${this.person2.firstname} ${this.person2.lastname}`.trim();
            return this.selectedPerson2Label || this.$t('relationshipTree.personBFallback');
        },
        relationshipQuestionTitle() {
            return this.getRelationshipTitle(this.personName1, this.personName2);
        },
        relationshipTitleParts() {
            const titleTemplate = this.getRelationshipTitleTemplate('{personA}', '{personB}');
            const tokenPattern = /\{personA\}|\{personB\}/g;
            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = tokenPattern.exec(titleTemplate)) !== null) {
                if (match.index > lastIndex) {
                    parts.push({
                        type: 'text',
                        text: titleTemplate.slice(lastIndex, match.index),
                        key: `text-${lastIndex}`,
                    });
                }

                const isPersonA = match[0] === '{personA}';
                parts.push({
                    type: 'person',
                    personNumber: isPersonA ? 1 : 2,
                    key: isPersonA ? 'person-a' : 'person-b',
                });
                lastIndex = match.index + match[0].length;
            }

            if (lastIndex < titleTemplate.length) {
                parts.push({
                    type: 'text',
                    text: titleTemplate.slice(lastIndex),
                    key: `text-${lastIndex}`,
                });
            }

            return parts.length ? parts : [
                { type: 'person', personNumber: 1, key: 'person-a' },
                { type: 'text', text: ' en ', key: 'fallback-joiner' },
                { type: 'person', personNumber: 2, key: 'person-b' },
            ];
        },
        hasSelectedPair() {
            return !!(this.selectedPerson1Id && this.selectedPerson2Id);
        },
        isSamePersonSelected() {
            return this.hasSelectedPair && String(this.selectedPerson1Id) === String(this.selectedPerson2Id);
        },
        showSuggestions1() {
            return this.activeInlineEditor === 1 && this.personSuggestions1.length > 0;
        },
        showSuggestions2() {
            return this.activeInlineEditor === 2 && this.personSuggestions2.length > 0;
        },
        pickerHint() {
            if (!this.selectedPerson1Id && !this.selectedPerson2Id) {
                return '';
            }
            if (!this.selectedPerson1Id || !this.selectedPerson2Id) {
                return this.$t('relationshipTree.selectAnotherPerson');
            }
            if (this.relationshipFound === false) {
                return this.$t('relationshipTree.noMatchFound');
            }
            return '';
        },
        // Het actieve pad op basis van het geselecteerde tabblad
        activePath() {
            return this.allPaths[this.activePathIndex] || [];
        },
        graphPath() {
            return this.expandSiblingEdgesForGraph(this.activePath);
        },
        // De computed property die de natuurlijke taalversie van de relatie berekent:
        naturalRelationship() {
            if (this.activePath && this.activePath.length > 1) {
                const fromGrid = this.getRelationshipLabelFromGrid();
                if (fromGrid) return fromGrid;
                return computeDutchRelation(this.activePath);
            }
            return '';
        },
        // ============================================================
        // Dynamic grid layout derived from the API path. Compact sibling edges are
        // expanded with their shared parent context for rendering only.
        // Rules:
        //   via='parent'              → go UP    (col stays, row++)
        //   via='child'               → go DOWN  (row--; col++ only at LCA transition)
        //   via='spouse'|'*Sibling'   → go RIGHT with spacer (col += 2, spacer at col+1)
        // ============================================================
        gridLayout() {
            if (!this.graphPath || this.graphPath.length === 0) return null;

            const path = this.graphPath;
            const bloodLcaIds = Array.isArray(this.graphData?.lcaIds)
                ? this.graphData.lcaIds.map(id => String(id))
                : [];
            const firstChildIdx = path.findIndex((s, j) => j > 0 && s.via === 'child');
            const primaryBloodLcaIdx = this.graphData?.mode === 'blood'
                ? path.findIndex((s, j) => j > 0 && s.via === 'child' && bloodLcaIds.includes(String(path[j - 1]?.personID)))
                : -1;
            const lcaAnchorIdx = primaryBloodLcaIdx >= 0 ? primaryBloodLcaIdx : firstChildIdx;
            const cells = [];            // person cells
            const spacers = [];           // label columns between lateral neighbours
            const edges = [];             // path edges (parent→child chain)
            const siblingLines = [];      // extra horizontal connectors at sibling rows
            const siblingSpacerMeta = []; // metadata for cousin-label filling
            const spacerColSet = new Set();

            const siblingLabel = (sexA, sexB, useHalf = false) => {
                const g1 = (sexA || '').toUpperCase();
                const g2 = (sexB || '').toUpperCase();
                if (g1 === 'M' && g2 === 'M') return useHalf ? this.$t('relationshipTree.siblings.halfBrothers') : this.$t('relationshipTree.siblings.brothers');
                if (g1 === 'F' && g2 === 'F') return useHalf ? this.$t('relationshipTree.siblings.halfSisters') : this.$t('relationshipTree.siblings.sisters');
                return useHalf ? this.$t('relationshipTree.siblings.halfMixed') : this.$t('relationshipTree.siblings.mixed');
            };

            const cousinLabel = (degree, sexA, sexB, useHalf = false) => {
                const g1 = (sexA || '').toUpperCase();
                const g2 = (sexB || '').toUpperCase();
                const bothM = g1 === 'M' && g2 === 'M';
                const bothF = g1 === 'F' && g2 === 'F';
                if (degree === 1) {
                    if (bothM) return useHalf ? this.$t('relationshipTree.cousins.halfMaleCousinsDegree1') : this.$t('relationshipTree.cousins.maleCousinsDegree1');
                    if (bothF) return useHalf ? this.$t('relationshipTree.cousins.halfFemaleCousinsDegree1') : this.$t('relationshipTree.cousins.femaleCousinsDegree1');
                    return useHalf ? this.$t('relationshipTree.cousins.halfMixedCousinsDegree1') : this.$t('relationshipTree.cousins.mixedCousinsDegree1');
                }
                if (degree === 2) {
                    if (bothM) return useHalf ? this.$t('relationshipTree.cousins.halfMaleCousinsDegree2') : this.$t('relationshipTree.cousins.maleCousinsDegree2');
                    if (bothF) return useHalf ? this.$t('relationshipTree.cousins.halfFemaleCousinsDegree2') : this.$t('relationshipTree.cousins.femaleCousinsDegree2');
                    return useHalf ? this.$t('relationshipTree.cousins.halfMixedCousinsDegree2') : this.$t('relationshipTree.cousins.mixedCousinsDegree2');
                }
                const base = bothM
                    ? (useHalf ? this.$t('relationshipTree.cousins.halfMaleCousinsDegree2') : this.$t('relationshipTree.cousins.maleCousinsDegree2'))
                    : bothF
                        ? (useHalf ? this.$t('relationshipTree.cousins.halfFemaleCousinsDegree2') : this.$t('relationshipTree.cousins.femaleCousinsDegree2'))
                        : (useHalf ? this.$t('relationshipTree.cousins.halfMixedCousinsDegree2') : this.$t('relationshipTree.cousins.mixedCousinsDegree2'));
                return this.$t('relationshipTree.cousins.distantDegree', { base, degree: this.degreeWord(degree) });
            };

            const getTransitionPairInfo = (transitionIdx) => {
                // transitionIdx points to the 'child' step in a parent -> child turn.
                const lcaId = path[transitionIdx - 1]?.personID;
                const leftChildId = path[transitionIdx - 2]?.personID;
                const rightChildId = path[transitionIdx]?.personID;
                const siblingBridge = path[transitionIdx - 1]?.siblingBridge;

                if (siblingBridge === 'fullSibling' || siblingBridge === 'halfSibling') {
                    return {
                        isHalf: siblingBridge === 'halfSibling',
                        pair: siblingBridge === 'fullSibling'
                            ? (path[transitionIdx - 1]?.siblingPair || null)
                            : null,
                    };
                }

                if (transitionIdx === lcaAnchorIdx && this.graphData?.mode === 'blood') {
                    const isHalf = this.graphData?.lcaNodes?.length === 1;
                    const pair = this.graphData?.lcaNodes?.length === 2
                        ? (this.graphData.lcaNodes.find(n => String(n.personID) !== String(lcaId)) || null)
                        : null;
                    return { isHalf, pair };
                }

                const byTransition = this.graphData?.lcaPairByTransition || {};
                const leftPair = lcaId && leftChildId ? byTransition[`${lcaId}|${leftChildId}`] : null;
                const rightPair = lcaId && rightChildId ? byTransition[`${lcaId}|${rightChildId}`] : null;

                if (leftPair && rightPair && String(leftPair.personID) === String(rightPair.personID)) {
                    return { isHalf: false, pair: rightPair };
                }
                return { isHalf: true, pair: null };
            };

            const getSpousePairKey = (personAId, personBId) => {
                return [String(personAId || ''), String(personBId || '')].sort().join('|');
            };

            let col = 0, row = 0;
            cells.push({ person: path[0], col, row });

            for (let i = 1; i < path.length; i++) {
                const step = path[i];
                const via = step.via;
                const prevVia = path[i - 1].via;

                if (via === 'parent') {
                    row++;
                } else if (via === 'child') {
                    if (prevVia === 'parent') {
                        // LCA transition: descending back to a row that already has a person.
                        // Insert a spacer column between that peer and the incoming person.
                        const newRow = row - 1;
                        const spacerCol = col + 1;
                        const leftCol = spacerCol - 1;
                        spacerColSet.add(spacerCol);
                        col += 2;
                        const rightCol = spacerCol + 1;
                        const peerCellIdx = cells.findIndex(c => c.row === newRow && c.col === leftCol);
                        const peerCell = peerCellIdx !== -1 ? cells[peerCellIdx] : null;
                        if (peerCell) {
                            const { isHalf: isHalfAtTransition } = getTransitionPairInfo(i);
                            const label = siblingLabel(peerCell.person.sex, step.sex, isHalfAtTransition);
                            spacers.push({ col: spacerCol, row: newRow, label });
                            // extra horizontal connector at the sibling row
                            siblingLines.push({ fromIdx: peerCellIdx, toIdx: cells.length });
                            // remember this sibling spacer so we can fill cousin labels below
                            siblingSpacerMeta.push({
                                spacerCol,
                                siblingRow: newRow,
                                leftCol,
                                rightCol,
                                isHalf: isHalfAtTransition,
                            });
                        }
                    }
                    row--;
                } else {
                    // spouse / fullSibling / halfSibling (BFS-fallback path): insert a spacer column
                    const spacerCol = col + 1;
                    spacerColSet.add(spacerCol);
                    col += 2;

                    const fromPerson = path[i - 1];
                    const toPerson = step;
                    let label;
                    if (via === 'spouse') {
                        const pairKey = getSpousePairKey(fromPerson.personID, toPerson.personID);
                        label = this.graphData?.spouseLabelByPair?.[pairKey] || this.$t('relationshipTree.relationships.married');
                    } else {
                        label = siblingLabel(fromPerson.sex, toPerson.sex, via === 'halfSibling');
                    }
                    spacers.push({ col: spacerCol, row, label });
                }

                cells.push({ person: step, col, row });
                edges.push({ from: i - 1, to: i });
            }

            // Fill cousin labels below each sibling spacer where both branches have descendants
            if (siblingSpacerMeta.length > 0) {
                const cellByPos = new Map();
                cells.forEach((c, idx) => cellByPos.set(`${c.col},${c.row}`, idx));

                for (const meta of siblingSpacerMeta) {
                    const { spacerCol, siblingRow, leftCol, rightCol, isHalf } = meta;
                    let degree = 1;
                    for (let r = siblingRow - 1; r >= 0; r--, degree++) {
                        // Cousin labels only apply when descendants remain directly adjacent to this sibling spacer.
                        if (leftCol !== spacerCol - 1 || rightCol !== spacerCol + 1) break;
                        const leftIdx = cellByPos.get(`${leftCol},${r}`);
                        const rightIdx = cellByPos.get(`${rightCol},${r}`);
                        if (leftIdx === undefined || rightIdx === undefined) break;

                        const label = cousinLabel(
                            degree,
                            cells[leftIdx].person.sex,
                            cells[rightIdx].person.sex,
                            isHalf,
                        );
                        spacers.push({ col: spacerCol, row: r, label });
                        siblingLines.push({ fromIdx: leftIdx, toIdx: rightIdx });
                    }
                }
            }

            // Center every LCA node on its sibling spacer column and tag adjacent edges
            // for Y-fork rendering. Applies to all parent→child transitions anywhere in
            // the path, covering blood LCAs and ancestors of spouses further right.
            for (let i = 1; i < path.length; i++) {
                if (path[i].via === 'child' && path[i - 1]?.via === 'parent') {
                    const lcaCell = cells[i - 1];
                    // Primary blood MRCA must follow lcaNodes (so half relations stay single).
                    // Secondary LCAs (e.g., spouse ancestors) may use path-derived co-parent data.
                    const { pair } = getTransitionPairInfo(i);
                    if (pair) lcaCell.lcaPair = pair;
                    // Move cell to the spacer column centred between the two descent columns.
                    const centeredCol = lcaCell.col + 1;
                    lcaCell.col = centeredCol;
                    spacerColSet.add(centeredCol);
                    // Tag both adjacent edges for Y-fork SVG rendering.
                    if (i >= 2) edges[i - 2].isMrcaEdge = true;
                    edges[i - 1].isMrcaEdge = true;
                }
            }

            // Shift rows so minimum is 0
            const allRows = [...cells.map(c => c.row), ...spacers.map(s => s.row)];
            const minRow = Math.min(...allRows);
            if (minRow < 0) {
                cells.forEach(c => { c.row -= minRow; });
                spacers.forEach(s => { s.row -= minRow; });
            }

            const maxRow = Math.max(...cells.map(c => c.row), 0);
            const maxCol = Math.max(...cells.map(c => c.col), ...spacers.map(s => s.col), 0);
            const numCols = maxCol + 1;
            const numRows = maxRow + 1;

            // Build per-column width array and precompute centre-X positions
            const colWidths = [];
            for (let c = 0; c < numCols; c++) {
                colWidths.push(spacerColSet.has(c) ? REL_SPACER_W : REL_CELL_W);
            }
            const colCenterX = [];
            let xAccum = 0;
            for (let c = 0; c < numCols; c++) {
                colCenterX.push(xAccum + colWidths[c] / 2);
                xAccum += colWidths[c] + REL_GAP_X;
            }
            const totalWidth = xAccum - REL_GAP_X;

            return { cells, spacers, edges, siblingLines, numCols, numRows, colWidths, colCenterX, totalWidth };
        },

        gridAvailable() {
            return this.gridLayout !== null;
        },

        relGridWidth() {
            return this.gridLayout?.totalWidth ?? 0;
        },

        relGridHeight() {
            if (!this.gridLayout) return 0;
            const { numRows } = this.gridLayout;
            return numRows * REL_CELL_H + (numRows - 1) * REL_GAP_Y;
        },

        relGridContainerStyle() {
            return {
                position: 'relative',
                width: `${this.relGridWidth}px`,
                height: `${this.relGridHeight}px`,
                transform: `scale(${this.relGridFitScale})`,
                transformOrigin: 'top left',
            };
        },

        relGridFitStyle() {
            return {
                position: 'relative',
                width: `${this.relGridWidth * this.relGridFitScale}px`,
                height: `${this.relGridHeight * this.relGridFitScale}px`,
                margin: '0 auto',
            };
        },

        relGridStyle() {
            if (!this.gridLayout) return {};
            const { colWidths, numRows } = this.gridLayout;
            return {
                display: 'grid',
                gridTemplateColumns: colWidths.map(w => `${w}px`).join(' '),
                gridTemplateRows: `repeat(${numRows}, ${REL_CELL_H}px)`,
                columnGap: `${REL_GAP_X}px`,
                rowGap: `${REL_GAP_Y}px`,
                position: 'absolute',
                top: '0',
                left: '0',
            };
        },

        isDirectLine() {
            if (!this.activePath || this.activePath.length < 2) return true;
            return !this.activePath.some(
                (s, i) => i > 0 && (s.via === 'child' || s.via === 'spouse' || s.via === 'fullSibling' || s.via === 'halfSibling')
            );
        },

        isAffinal() {
            return this.graphData?.mode === 'affinal' ||
                (this.activePath && this.activePath.some((s, i) => i > 0 && s.via === 'spouse'));
        },
    },
    watch: {
        personName1() {
            this.updateDocumentTitle();
        },
        personName2() {
            this.updateDocumentTitle();
        },
        '$route.fullPath'() {
            if (this.ignoreNextRouteSync) {
                this.ignoreNextRouteSync = false;
                return;
            }
            this.initializeFromContext();
        },
    },
    methods: {
        formatYears,

        getRelationshipTitleTemplate(personA, personB) {
            const title = this.$t('relationshipTree.titleTemplate', { personA, personB });
            if (title === 'relationshipTree.titleTemplate') {
                return `Hoe zijn ${personA} en ${personB} familie van elkaar?`;
            }
            return title;
        },

        getRelationshipTitle(personA, personB) {
            return this.getRelationshipTitleTemplate(personA, personB);
        },

        getTitlePersonName(which) {
            return which === 1 ? this.personName1 : this.personName2;
        },

        getPersonSearchQuery(which) {
            return which === 1 ? this.personSearchQuery1 : this.personSearchQuery2;
        },

        getPersonSearchPlaceholder(which) {
            return which === 1
                ? this.$t('relationshipTree.searchPersonA')
                : this.$t('relationshipTree.searchPersonB');
        },

        getPersonSuggestions(which) {
            return which === 1 ? this.personSuggestions1 : this.personSuggestions2;
        },

        shouldShowSuggestions(which) {
            return which === 1 ? this.showSuggestions1 : this.showSuggestions2;
        },

        handlePersonSearchInput(which, event) {
            if (which === 1) {
                this.personSearchQuery1 = event.target.value;
            } else {
                this.personSearchQuery2 = event.target.value;
            }
            this.onPersonSearchInput(which);
        },

        buildPersonLabel(person) {
            if (!person) return '';
            const first = String(person.firstname || '').trim();
            const last = String(person.lastname || '').trim();
            const fullName = `${first} ${last}`.trim();
            if (fullName) return fullName;
            return String(person.personID || person.id || '').trim();
        },

        suggestionLabel(person) {
            return this.buildPersonLabel(person);
        },

        suggestionMeta(person) {
            const years = this.formatYears(
                person.birthdatetr,
                person.altbirthdatetr,
                person.deathdatetr,
                person.burialdatetr,
                person.living,
            );
            const personId = person.personID ? `ID: ${person.personID}` : '';
            return [years, personId].filter(Boolean).join(' • ');
        },

        resolveRootPersonFromCategory(category) {
            return this.dnaTesters[category]?.rootPersonID || DEFAULT_RELATIONSHIP_ROOT;
        },

        async refreshDnaTesters() {
            try {
                const response = await fetchDnaTesters();
                const testers = Array.isArray(response?.testers) ? response.testers : [];
                this.dnaTesters = Object.fromEntries(
                    testers.map(tester => [tester.category, tester])
                );
            } catch (error) {
                this.dnaTesters = {};
                console.error('Error loading privacy-filtered DNA testers:', error);
            }
        },

        resolveInitialPairFromContext() {
            const routeParams = this.$route.params || {};
            const routeQuery = this.$route.query || {};

            let personID1 = routeParams.personID1 || routeQuery.personID1 || routeQuery.personA || '';
            let personID2 = routeParams.personID2 || routeQuery.personID2 || routeQuery.personB || '';

            if ((!personID1 || !personID2) && routeQuery.source === 'person' && routeQuery.personId) {
                personID1 = personID1 || DEFAULT_RELATIONSHIP_ROOT;
                personID2 = personID2 || String(routeQuery.personId);
            }

            if ((!personID1 || !personID2) && routeQuery.source === 'dna' && routeQuery.matchPersonID) {
                personID1 = personID1 || this.resolveRootPersonFromCategory(routeQuery.category);
                personID2 = personID2 || String(routeQuery.matchPersonID);
            }

            if (!personID1 && personID2) {
                personID1 = DEFAULT_RELATIONSHIP_ROOT;
            }

            return {
                personID1: personID1 ? String(personID1) : null,
                personID2: personID2 ? String(personID2) : null,
            };
        },

        resetRelationshipState() {
            this.relationshipFound = null;
            this.allPaths = [];
            this.activePathIndex = 0;
            this.relationshipSummary = '';
            this.error = null;
            this.graphLoading = false;
            this.graphError = null;
            this.graphData = null;
            this.person1 = null;
            this.person2 = null;
        },

        async initializeFromContext() {
            const { personID1, personID2 } = this.resolveInitialPairFromContext();

            this.selectedPerson1Id = personID1;
            this.selectedPerson2Id = personID2;

            if (personID1 && personID2) {
                await this.loadData(personID1, personID2);
                return;
            }

            this.resetRelationshipState();
            await this.hydrateSelectedLabels();
        },

        async hydrateSelectedLabels() {
            const tasks = [];
            if (this.selectedPerson1Id && !this.selectedPerson1Label) {
                tasks.push(
                    getPersonById(this.selectedPerson1Id)
                        .then((person) => {
                            this.selectedPerson1Label = this.buildPersonLabel(person);
                            this.personSearchQuery1 = this.selectedPerson1Label;
                        })
                        .catch(() => null)
                );
            }
            if (this.selectedPerson2Id && !this.selectedPerson2Label) {
                tasks.push(
                    getPersonById(this.selectedPerson2Id)
                        .then((person) => {
                            this.selectedPerson2Label = this.buildPersonLabel(person);
                            this.personSearchQuery2 = this.selectedPerson2Label;
                        })
                        .catch(() => null)
                );
            }
            if (tasks.length) await Promise.all(tasks);
        },

        startInlineEdit(which) {
            this.activeInlineEditor = which;
            this.pickerError = null;
            this.personSuggestions1 = which === 1 ? this.personSuggestions1 : [];
            this.personSuggestions2 = which === 2 ? this.personSuggestions2 : [];
            // Behave like the header search box: open + immediately populate suggestions.
            this.onPersonSearchInput(which);
            this.$nextTick(() => {
                const refName = which === 1 ? 'inlineInput1' : 'inlineInput2';
                const inputRef = this.$refs[refName];
                const inputEl = Array.isArray(inputRef) ? inputRef[0] : inputRef;
                if (inputEl && typeof inputEl.focus === 'function') {
                    inputEl.focus();
                    inputEl.select?.();
                }
            });
        },

        cancelInlineEdit() {
            this.activeInlineEditor = null;
            this.personSuggestions1 = [];
            this.personSuggestions2 = [];
            this.personSearchQuery1 = this.selectedPerson1Label || this.personSearchQuery1;
            this.personSearchQuery2 = this.selectedPerson2Label || this.personSearchQuery2;
        },

        handleClickOutsidePicker(event) {
            const inlineEditorRoot = this.$refs.inlineEditorRoot;
            if (!inlineEditorRoot) return;
            const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
            const clickedInside = path.includes(inlineEditorRoot) || inlineEditorRoot.contains(event.target);
            if (!clickedInside) {
                this.cancelInlineEdit();
            }
        },

        onPersonSearchInput(which) {
            this.pickerError = null;
            this.activeInlineEditor = which;
            const query = which === 1 ? this.personSearchQuery1 : this.personSearchQuery2;
            const trimmed = String(query || '').trim();

            if (which === 1) {
                if (trimmed !== this.selectedPerson1Label) this.selectedPerson1Id = null;
                clearTimeout(this.searchDebounceTimer1);
            } else {
                if (trimmed !== this.selectedPerson2Label) this.selectedPerson2Id = null;
                clearTimeout(this.searchDebounceTimer2);
            }

            if (trimmed.length < 2) {
                if (which === 1) this.personSuggestions1 = [];
                else this.personSuggestions2 = [];
                return;
            }

            const timer = setTimeout(async () => {
                try {
                    const response = await searchPeopleSuggestions(trimmed);
                    const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
                    if (which === 1) this.personSuggestions1 = suggestions;
                    else this.personSuggestions2 = suggestions;
                } catch (err) {
                    if (which === 1) this.personSuggestions1 = [];
                    else this.personSuggestions2 = [];
                }
            }, 220);

            if (which === 1) this.searchDebounceTimer1 = timer;
            else this.searchDebounceTimer2 = timer;
        },

        selectSuggestedPerson(which, suggestion) {
            const personID = String(suggestion?.personID || suggestion?.id || '');
            if (!personID) return;

            const label = this.buildPersonLabel(suggestion);
            if (which === 1) {
                this.selectedPerson1Id = personID;
                this.selectedPerson1Label = label;
                this.personSearchQuery1 = label;
                this.personSuggestions1 = [];
                this.person1 = null;
            } else {
                this.selectedPerson2Id = personID;
                this.selectedPerson2Label = label;
                this.personSearchQuery2 = label;
                this.personSuggestions2 = [];
                this.person2 = null;
            }
            this.pickerError = null;

            if (this.hasSelectedPair && !this.isSamePersonSelected) {
                this.applySelectedPair();
            }

            this.activeInlineEditor = null;
        },

        handleSuggestionAuxClick(which, suggestion, event) {
            if (event.button === 1) {
                this.selectSuggestedPerson(which, suggestion);
            }
        },

        handlePickerEnter(which) {
            const suggestions = which === 1 ? this.personSuggestions1 : this.personSuggestions2;
            if (suggestions.length > 0) {
                this.selectSuggestedPerson(which, suggestions[0], null);
                return;
            }
            const query = which === 1 ? this.personSearchQuery1 : this.personSearchQuery2;
            const trimmed = String(query || '').trim();
            const selectedLabel = which === 1 ? this.selectedPerson1Label : this.selectedPerson2Label;
            if (trimmed === selectedLabel) {
                this.activeInlineEditor = null;
            }
        },

        async applySelectedPair() {
            if (!this.hasSelectedPair) {
                this.pickerError = 'Selecteer eerst twee personen.';
                return;
            }
            if (this.isSamePersonSelected) {
                this.pickerError = 'Kies twee verschillende personen.';
                return;
            }

            this.pickerError = null;

            const personID1 = String(this.selectedPerson1Id);
            const personID2 = String(this.selectedPerson2Id);

            this.ignoreNextRouteSync = true;
            await this.$router.replace({
                path: `/relationship/${personID1}/${personID2}`,
                query: { source: 'manual' },
            });
            await this.loadData(personID1, personID2);
        },

        degreeWord(n) {
            const words = {
                2: this.$t('relationshipTree.degreeWords.2'),
                3: this.$t('relationshipTree.degreeWords.3'),
                4: this.$t('relationshipTree.degreeWords.4'),
                5: this.$t('relationshipTree.degreeWords.5'),
                6: this.$t('relationshipTree.degreeWords.6'),
                7: this.$t('relationshipTree.degreeWords.7'),
                8: this.$t('relationshipTree.degreeWords.8'),
                9: this.$t('relationshipTree.degreeWords.9'),
                10: this.$t('relationshipTree.degreeWords.10'),
            };
            return words[n] || `${n}`;
        },

        getRelationshipLabelFromGrid() {
            const layout = this.gridLayout;
            if (!layout || !Array.isArray(layout.spacers) || layout.spacers.length === 0) return null;

            const marriedLabel = this.$t('relationshipTree.relationships.married');
            const relationSpacers = layout.spacers.filter(s => s?.label && s.label !== marriedLabel);
            if (!relationSpacers.length) return null;

            const minRow = Math.min(...relationSpacers.map(s => s.row));
            const onBottomRow = relationSpacers.filter(s => s.row === minRow);
            if (!onBottomRow.length) return null;

            // Right-most label on the bottom relation row best matches the path target side.
            const best = onBottomRow.reduce((acc, cur) => (!acc || cur.col > acc.col ? cur : acc), null);
            if (!best?.label) return null;

            const hasSpouse = this.activePath?.some((step, idx) => idx > 0 && step?.via === 'spouse');
            let label = String(best.label).toLowerCase();
            if (hasSpouse) label = `${label} (aangetrouwd)`;
            return label;
        },

        updateRelGridFitScale() {
            if (!this.gridAvailable || !this.relGridWidth || !this.relGridHeight) {
                this.relGridFitScale = 1;
                return;
            }

            const scrollEl = this.$refs.relGridScroll;
            if (!scrollEl || typeof window === 'undefined') {
                this.relGridFitScale = 1;
                return;
            }

            const rect = scrollEl.getBoundingClientRect();
            const viewportPaddingX = 24;
            const viewportPaddingBottom = 24;
            const availableWidth = Math.max(window.innerWidth - viewportPaddingX * 2, 0);
            const availableHeight = Math.max(window.innerHeight - rect.top - viewportPaddingBottom, 0);

            if (!availableWidth || !availableHeight) {
                this.relGridFitScale = 1;
                return;
            }

            const widthScale = availableWidth / this.relGridWidth;
            const heightScale = availableHeight / this.relGridHeight;
            this.relGridFitScale = Math.min(1, widthScale, heightScale);
        },

        handleRelGridResize() {
            this.$nextTick(() => {
                this.updateRelGridFitScale();
            });
        },

        buildTreeExportFilename() {
            const left = [this.person1?.firstname, this.person1?.lastname].filter(Boolean).join(' ').trim();
            const right = [this.person2?.firstname, this.person2?.lastname].filter(Boolean).join(' ').trim();
            const rawName = `relatie-${left || 'persoon-1'}-${right || 'persoon-2'}`;
            const slug = rawName
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-zA-Z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .toLowerCase();
            return `${slug || 'relatieboom'}.png`;
        },

        async downloadTreeImage() {
            if (this.isExportingTree) return;

            const treeEl = this.$refs.relGridContainer;
            if (!treeEl) return;

            this.isExportingTree = true;
            const previousScale = this.relGridFitScale;

            try {
                this.relGridFitScale = 1;
                await this.$nextTick();

                const exportBackgroundColor = getComputedStyle(document.body).backgroundColor || '#111a18';

                // Create a temporary wrapper with padding for export
                const wrapper = document.createElement('div');
                wrapper.style.padding = '40px';
                wrapper.style.backgroundColor = exportBackgroundColor;
                wrapper.style.display = 'flex';
                wrapper.style.flexDirection = 'column';
                wrapper.style.width = 'fit-content';
                wrapper.style.margin = '0 auto';
                treeEl.parentNode.insertBefore(wrapper, treeEl);

                // Create title
                const title = document.createElement('h2');
                title.style.textAlign = 'center';
                title.style.fontSize = '18px';
                title.style.fontWeight = 'bold';
                title.style.color = '#fff';
                title.style.marginBottom = '30px';
                title.style.lineHeight = '1.4';
                title.textContent = this.relationshipQuestionTitle;
                wrapper.appendChild(title);

                wrapper.appendChild(treeEl);

                // Create watermark
                const watermark = document.createElement('div');
                watermark.style.textAlign = 'center';
                watermark.style.marginTop = '20px';
                watermark.style.fontSize = '12px';
                watermark.style.color = '#aaa';
                watermark.style.opacity = '0.6';
                watermark.style.lineHeight = '1.5';
                watermark.innerHTML = `
                    Familiegeschiedenis onderzoek en samenstelling:<br>
                    © Jeffrey Thümann 2026<br>
                    sejarah-thumann.online
                `;
                wrapper.appendChild(watermark);

                const canvas = await html2canvas(wrapper, {
                    backgroundColor: exportBackgroundColor,
                    scale: Math.max(2, window.devicePixelRatio || 1),
                    useCORS: true,
                    logging: false,
                    onclone: (clonedDoc) => {
                        const textSelectors = [
                            '.PersonCard_name',
                            '.PersonCard_years',
                            '.rel-grid-spacer-label',
                        ];
                        clonedDoc.querySelectorAll(textSelectors.join(',')).forEach((el) => {
                            el.style.overflow = 'visible';
                            el.style.textOverflow = 'clip';
                            el.style.lineHeight = '1.3';
                            el.style.paddingBottom = '0.15em';
                            el.style.transform = 'translateY(-8px)';
                        });

                        clonedDoc.querySelectorAll('.PersonCard_years img').forEach((el) => {
                            el.style.transform = 'translateY(8px)';
                        });

                        clonedDoc.querySelectorAll('.PersonCard_text_wrapper').forEach((el) => {
                            el.style.overflow = 'visible';
                        });
                    },
                });

                // Restore original DOM: move tree back out of wrapper and remove wrapper
                wrapper.parentNode.insertBefore(treeEl, wrapper);
                wrapper.parentNode.removeChild(wrapper);

                const link = document.createElement('a');
                link.href = canvas.toDataURL('image/png');
                link.download = this.buildTreeExportFilename();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('Fout bij exporteren van relatieboom:', error);
            } finally {
                this.relGridFitScale = previousScale;
                this.isExportingTree = false;
                this.$nextTick(() => {
                    this.updateRelGridFitScale();
                });
            }
        },

        // -------------------------------------------------------
        // Grid rendering helpers
        // -------------------------------------------------------

        /**
         * Returns the CSS grid-placement style for a cell.
         * Row 0 = bottom → mapped to CSS grid-row numRows (last row).
         */
        relCellPositionStyle(cell) {
            if (!this.gridLayout) return {};
            const { numRows } = this.gridLayout;
            return {
                gridColumn: cell.col + 1,
                gridRow: numRows - cell.row,
            };
        },

        /**
         * Builds an SVG path string connecting two grid cells.
         *   • Same col  → vertical line
         *   • Same row  → horizontal line
         *   • Diagonal  → L-shaped elbow (horizontal first, then vertical)
         */
        relEdgePath(edge) {
            const { cells, numRows, colCenterX } = this.gridLayout;
            const from = cells[edge.from];
            const to = cells[edge.to];

            const cx = col => colCenterX[col];
            const cy = row => (numRows - 1 - row) * (REL_CELL_H + REL_GAP_Y) + REL_CELL_H / 2;

            const x1 = cx(from.col), y1 = cy(from.row);
            const x2 = cx(to.col), y2 = cy(to.row);

            // Y-fork shape for edges adjacent to a centred MRCA:
            // go vertically to the midpoint between the two rows,
            // then bend horizontally to the MRCA centre-X,
            // then continue vertically to the destination.
            if (edge.isMrcaEdge && from.col !== to.col) {
                const R = 12;
                const FLARE = 20;
                const midY = (y1 + y2) / 2;
                const goRight = x2 > x1;
                const hDir = goRight ? 1 : -1;
                if (y1 > y2) {
                    // Ascending edge (child → MRCA): go UP, bend toward MRCA centre, go UP,
                    // then flare outward at the top so the line arrives offset from MRCA centre.
                    return `M ${x1} ${y1} V ${midY + R} Q ${x1} ${midY} ${x1 + hDir * R} ${midY} H ${x2 - hDir * R} Q ${x2} ${midY} ${x2} ${midY - R} V ${y2 + R} Q ${x2} ${y2} ${x2 - hDir * FLARE} ${y2}`;
                } else {
                    // Descending edge (MRCA → child): flare outward from MRCA top,
                    // then bend inward and go DOWN.
                    return `M ${x1 + hDir * FLARE} ${y1} Q ${x1} ${y1} ${x1} ${y1 + R} V ${midY - R} Q ${x1} ${midY} ${x1 + hDir * R} ${midY} H ${x2 - hDir * R} Q ${x2} ${midY} ${x2} ${midY + R} V ${y2}`;
                }
            }

            if (from.col === to.col) {
                return `M ${x1} ${y1} L ${x2} ${y2}`;
            }
            if (from.row === to.row) {
                return `M ${x1} ${y1} L ${x2} ${y2}`;
            }
            // L-shaped elbow (always moves right)
            const R = 12;
            if (y2 > y1) {
                return `M ${x1} ${y1} H ${x2 - R} Q ${x2} ${y1} ${x2} ${y1 + R} V ${y2}`;
            }
            return `M ${x1} ${y1} H ${x2 - R} Q ${x2} ${y1} ${x2} ${y1 - R} V ${y2}`;
        },

        /**
         * Horizontal connector between two sibling cells at the same row (blood path).
         */
        relSiblingLinePath(line) {
            const { cells, numRows, colCenterX } = this.gridLayout;
            const from = cells[line.fromIdx];
            const to = cells[line.toIdx];
            const cy = row => (numRows - 1 - row) * (REL_CELL_H + REL_GAP_Y) + REL_CELL_H / 2;
            const x1 = colCenterX[from.col], x2 = colCenterX[to.col];
            const y = cy(from.row);
            return `M ${x1} ${y} L ${x2} ${y}`;
        },

        // -------------------------------------------------------
        // Legacy helpers kept for data-loading logic
        // -------------------------------------------------------
        // De LCA is het knooppunt waar de richting omslaat van 'parent' (omhoog) naar 'child' (omlaag).
        extractLcaFromPath(path) {
            if (!path || path.length < 2) return null;
            for (let i = 1; i < path.length; i++) {
                if (path[i].via === 'child') return path[i - 1];
            }
            return path[path.length - 1];
        },
        // Geeft de volledige naam van de gemeenschappelijke voorouder in een pad terug.
        getPathLcaName(path) {
            const lca = this.extractLcaFromPath(path);
            if (!lca || !lca.firstname) return null;
            return `${lca.firstname} ${lca.lastname}`;
        },
        // Wissel naar een ander relatiepad-tabblad en herlaad de grafiek.
        async switchPath(index) {
            this.activePathIndex = index;
            const personID1 = this.selectedPerson1Id;
            const personID2 = this.selectedPerson2Id;
            if (personID1 && personID2) {
                await this.loadGraphData(personID1, personID2);
            }
        },
        updateDocumentTitle() {
            if (this.hasSelectedPair) {
                document.title = `${this.relationshipQuestionTitle} | Sejarah Thümann`;
            } else {
                document.title = `${this.$t('relationshipTree.defaultTitle')} | Sejarah Thümann`;
            }
        },
        // Bepaalt wat de naam is: als geen authToken en de persoon leeft (en niet een uitzondering is),
        // dan tonen we een privacybericht.
        displayName(person) {
            if (isLivingPlaceholder(person) || isPrivateLivingPerson(person, this.authToken)) {
                return this.$t('privacy.livingPlaceholder');
            }
            return `${person.firstname} ${person.lastname}`;
        },
        shouldHide(person) {
            return isLivingPlaceholder(person) || isPrivateLivingPerson(person, this.authToken);
        },
        buildAncestorIndex(rootPerson) {
            const map = new Map();
            const visit = (person, depth, childId) => {
                if (!person || !person.personID) return;
                const existing = map.get(person.personID);
                if (!existing || depth < existing.depth) {
                    map.set(person.personID, {
                        person,
                        depth,
                        childId,
                    });
                }
                const ancestors = Array.isArray(person.ancestors) ? person.ancestors : [];
                ancestors.forEach((ancestor) => visit(ancestor, depth + 1, person.personID));
            };
            visit(rootPerson, 0, null);
            return map;
        },
        getParentsFromAncestors(rootPerson) {
            const parents = Array.isArray(rootPerson?.ancestors) ? rootPerson.ancestors : [];
            return parents.filter(parent => parent && parent.personID).sort((a, b) => {
                return String(a.personID).localeCompare(String(b.personID));
            });
        },
        getCommonAncestors(indexA, indexB) {
            const candidates = [];
            indexA.forEach((valueA, id) => {
                if (!indexB.has(id)) return;
                const valueB = indexB.get(id);
                candidates.push({
                    id,
                    depthSum: valueA.depth + valueB.depth,
                    depthA: valueA.depth,
                    depthB: valueB.depth,
                });
            });
            return candidates;
        },
        selectLcaGroup(indexA, indexB) {
            const candidates = this.getCommonAncestors(indexA, indexB);
            if (candidates.length === 0) return { lcaIds: [] };
            const minDepthSum = Math.min(...candidates.map(candidate => candidate.depthSum));
            const minCandidates = candidates
                .filter(candidate => candidate.depthSum === minDepthSum)
                .sort((a, b) => String(a.id).localeCompare(String(b.id)));

            let lcaIds = [minCandidates[0].id];
            if (minCandidates.length >= 2) {
                for (let i = 0; i < minCandidates.length; i++) {
                    for (let j = i + 1; j < minCandidates.length; j++) {
                        const idA = minCandidates[i].id;
                        const idB = minCandidates[j].id;
                        const entryA = indexA.get(idA);
                        const entryB = indexA.get(idB);
                        const entryAOther = indexB.get(idA);
                        const entryBOther = indexB.get(idB);
                        if (!entryA?.childId || !entryB?.childId || !entryAOther?.childId || !entryBOther?.childId) {
                            continue;
                        }
                        if (entryA.childId === entryB.childId && entryAOther.childId === entryBOther.childId) {
                            lcaIds = [idA, idB].sort((a, b) => String(a).localeCompare(String(b)));
                            return { lcaIds };
                        }
                    }
                }
            }
            return { lcaIds };
        },
        pickBranchStart(index, lcaIds) {
            if (!lcaIds || lcaIds.length === 0) return null;
            if (lcaIds.length === 1) return lcaIds[0];
            const [first, second] = lcaIds;
            const entryFirst = index.get(first);
            const entrySecond = index.get(second);
            if (!entryFirst) return second;
            if (!entrySecond) return first;
            if (entryFirst.depth !== entrySecond.depth) {
                return entryFirst.depth < entrySecond.depth ? first : second;
            }
            return String(first).localeCompare(String(second)) <= 0 ? first : second;
        },
        buildBranch(index, startId) {
            if (!startId || !index.has(startId)) return [];
            const branch = [];
            const visited = new Set();
            let currentId = startId;
            while (currentId && !visited.has(currentId)) {
                visited.add(currentId);
                const node = index.get(currentId);
                if (!node || !node.person) break;
                branch.push(node.person);
                currentId = node.childId;
            }
            return branch;
        },
        getRowLabel(depth) {
            const isHalf = this.graphData?.lcaNodes?.length === 1;
            if (depth === 1) {
                if (isHalf || this.graphData?.sideParents) {
                    return 'halfbroers/halfzussen';
                }
                return 'broers/zussen';
            }
            const prefix = isHalf ? 'half ' : '';
            return `${prefix}${depth - 1}e graads neven/nichten`;
        },
        getAffinalLabel(label) {
            if (!label) return null;
            return `via ${label}`;
        },
        normalizeParentForCard(parent) {
            if (!parent) return null;
            const normalized = { ...parent };
            if (!normalized.firstname && !normalized.lastname && normalized.name) {
                const parts = String(normalized.name).trim().split(/\s+/);
                normalized.firstname = parts.shift() || '';
                normalized.lastname = parts.join(' ');
            }
            if (!normalized.birthdatetr && normalized.birthdate) {
                normalized.birthdatetr = normalized.birthdate;
            }
            if (!normalized.deathdatetr && normalized.deathdate) {
                normalized.deathdatetr = normalized.deathdate;
            }
            if (normalized.living === undefined || normalized.living === null) {
                normalized.living = false;
            }
            return normalized;
        },
        expandSiblingEdgesForGraph(path) {
            if (!Array.isArray(path) || path.length < 2) return path || [];

            const expanded = [path[0]];
            for (let index = 1; index < path.length; index++) {
                const step = path[index];
                const isSibling = step?.via === 'fullSibling' || step?.via === 'halfSibling';
                const sharedParents = isSibling && Array.isArray(step.sharedParents)
                    ? step.sharedParents
                        .map(parent => this.normalizeParentForCard(parent))
                        .filter(Boolean)
                        .sort((a, b) => {
                            const sexOrder = { M: 0, F: 1 };
                            const sexDifference = (sexOrder[a.sex] ?? 2) - (sexOrder[b.sex] ?? 2);
                            if (sexDifference !== 0) return sexDifference;
                            return String(a.personID || a.nodeID || '').localeCompare(String(b.personID || b.nodeID || ''));
                        })
                    : [];

                if (sharedParents.length === 0) {
                    expanded.push(step);
                    continue;
                }

                const [primaryParent, secondParent] = sharedParents;
                expanded.push({
                    ...primaryParent,
                    via: 'parent',
                    label: primaryParent.sex === 'M' ? 'vader' : 'moeder',
                    siblingBridge: step.via,
                    siblingPair: secondParent || null,
                });
                expanded.push({
                    ...step,
                    via: 'child',
                    label: step.sex === 'M' ? 'zoon' : 'dochter',
                });
            }
            return expanded;
        },
        getSpousePairKey(personAId, personBId) {
            return [String(personAId || ''), String(personBId || '')].sort().join('|');
        },
        hasMarriageInfo(familyInfo) {
            if (!familyInfo) return false;
            const rawDate = String(familyInfo.marrdatetr || '').trim();
            const rawPlace = String(familyInfo.marrplace || '').trim();
            const hasDate = rawDate !== '' && rawDate !== '0000-00-00';
            const hasPlace = rawPlace !== '';
            return hasDate || hasPlace;
        },
        async buildSpouseLabelMapFromPath(path) {
            const result = {};
            if (!Array.isArray(path) || path.length < 2) return result;

            const seenPersons = new Map();
            const spousePairs = [];

            for (let i = 1; i < path.length; i++) {
                if (path[i]?.via !== 'spouse') continue;
                const fromPerson = path[i - 1];
                const toPerson = path[i];
                if (!fromPerson?.personID || !toPerson?.personID) continue;
                const pairKey = this.getSpousePairKey(fromPerson.personID, toPerson.personID);
                spousePairs.push({ pairKey, fromPersonId: fromPerson.personID, toPersonId: toPerson.personID });
                if (!seenPersons.has(fromPerson.personID)) seenPersons.set(fromPerson.personID, fromPerson.personID);
                if (!seenPersons.has(toPerson.personID)) seenPersons.set(toPerson.personID, toPerson.personID);
            }

            if (!spousePairs.length) return result;

            const familyDetailsEntries = await Promise.all(
                Array.from(seenPersons.keys()).map(async (personId) => {
                    try {
                        const details = await getFamilyDetails(personId);
                        return [personId, details];
                    } catch (err) {
                        return [personId, null];
                    }
                })
            );

            const familyDetailsByPerson = new Map(familyDetailsEntries);

            for (const pair of spousePairs) {
                const { pairKey, fromPersonId, toPersonId } = pair;
                if (result[pairKey]) continue;

                const candidates = [
                    familyDetailsByPerson.get(fromPersonId),
                    familyDetailsByPerson.get(toPersonId),
                ];

                let matchingFamily = null;
                for (const details of candidates) {
                    const familyInfoList = Array.isArray(details?.familyInfo) ? details.familyInfo : [];
                    matchingFamily = familyInfoList.find(info => {
                        const husband = String(info?.husband || '');
                        const wife = String(info?.wife || '');
                        return this.getSpousePairKey(husband, wife) === pairKey;
                    });
                    if (matchingFamily) break;
                }

                result[pairKey] = this.hasMarriageInfo(matchingFamily) ? 'getrouwd' : 'partners';
            }

            return result;
        },
        async buildLcaPairMapFromPath(path) {
            const byNodeId = {};
            const byTransition = {};
            if (!Array.isArray(path) || path.length < 2) {
                return { byNodeId, byTransition };
            }

            const tasks = [];
            const seen = new Set();

            for (let i = 1; i < path.length; i++) {
                const step = path[i];
                const prev = path[i - 1];
                if (step?.via !== 'child' || prev?.via !== 'parent') continue;
                const lcaId = prev?.personID;
                const rightChildId = step?.personID;
                const leftChildId = path[i - 2]?.personID;
                if (!lcaId) continue;

                const childCandidates = [rightChildId, leftChildId].filter(Boolean);
                for (const childId of childCandidates) {
                    const key = `${lcaId}|${childId}`;
                    if (seen.has(key)) continue;
                    seen.add(key);

                    tasks.push((async () => {
                        try {
                            const parents = await getParents(childId);
                            if (!Array.isArray(parents) || parents.length < 2) return;
                            const includesLca = parents.some(parent => String(parent?.personID) === String(lcaId));
                            if (!includesLca) return;
                            const partner = parents.find(parent => parent?.personID && String(parent.personID) !== String(lcaId));
                            if (!partner) return;
                            const normalized = this.normalizeParentForCard(partner);
                            byTransition[key] = normalized;
                            if (!byNodeId[lcaId]) byNodeId[lcaId] = normalized;
                        } catch (err) {
                            // Missing parent records should not block graph rendering.
                        }
                    })());
                }
            }

            await Promise.all(tasks);
            return { byNodeId, byTransition };
        },
        async loadGraphData(personID1, personID2) {
            this.graphLoading = true;
            this.graphError = null;
            this.graphData = null;
            try {
                const generations = 10;
                const [ancA, ancB] = await Promise.all([
                    getAncestors(personID1, generations),
                    getAncestors(personID2, generations),
                ]);
                const indexA = this.buildAncestorIndex(ancA);
                const indexB = this.buildAncestorIndex(ancB);

                // Als er een actief pad is, gebruik de LCA uit dat pad als hint
                // zodat de grafiek het juiste tabblad weerspiegelt.
                const pathLcaNode = this.extractLcaFromPath(this.activePath);
                const fixedLcaId = pathLcaNode?.personID;
                const spouseLabelByPair = await this.buildSpouseLabelMapFromPath(this.activePath);
                const { byNodeId: lcaPairByNodeId, byTransition: lcaPairByTransition } = await this.buildLcaPairMapFromPath(this.activePath);

                let lcaIds;
                const fixedLcaIsCommon = fixedLcaId && indexA.has(fixedLcaId) && indexB.has(fixedLcaId);
                if (fixedLcaIsCommon) {
                    // Controleer of de LCA een partner heeft die ook gemeenschappelijke voorouder is
                    // (zodat niet-halve relaties het volledige ouderpaar tonen).
                    const candidates = this.getCommonAncestors(indexA, indexB);
                    const fixedCandidate = candidates.find(c => c.id === fixedLcaId);
                    let foundPair = false;
                    if (fixedCandidate) {
                        for (const other of candidates) {
                            if (other.id === fixedLcaId) continue;
                            if (other.depthA !== fixedCandidate.depthA || other.depthB !== fixedCandidate.depthB) continue;
                            const entryFixedA = indexA.get(fixedLcaId);
                            const entryOtherA = indexA.get(other.id);
                            const entryFixedB = indexB.get(fixedLcaId);
                            const entryOtherB = indexB.get(other.id);
                            if (!entryFixedA?.childId || !entryOtherA?.childId) continue;
                            if (!entryFixedB?.childId || !entryOtherB?.childId) continue;
                            if (entryFixedA.childId === entryOtherA.childId && entryFixedB.childId === entryOtherB.childId) {
                                lcaIds = [fixedLcaId, other.id].sort((a, b) => String(a).localeCompare(String(b)));
                                foundPair = true;
                                break;
                            }
                        }
                    }
                    if (!foundPair) {
                        lcaIds = fixedCandidate ? [fixedLcaId] : [];
                    }
                } else {
                    const result = this.selectLcaGroup(indexA, indexB);
                    lcaIds = result.lcaIds;
                }

                if (!lcaIds.length) {
                    if (this.activePath && this.activePath.length > 1) {
                        this.graphData = {
                            mode: 'affinal',
                            affinalPath: this.activePath,
                            spouseLabelByPair,
                            lcaPairByNodeId,
                            lcaPairByTransition,
                        };
                    }
                    return;
                }

                const lcaNodes = lcaIds
                    .map(id => indexA.get(id)?.person || indexB.get(id)?.person)
                    .filter(Boolean);
                const leftStart = this.pickBranchStart(indexA, lcaIds);
                const rightStart = this.pickBranchStart(indexB, lcaIds);
                const branchLeft = this.buildBranch(indexA, leftStart);
                const branchRight = this.buildBranch(indexB, rightStart);

                const parentsA = this.getParentsFromAncestors(ancA);
                const parentsB = this.getParentsFromAncestors(ancB);
                const parentsBMap = new Map(parentsB.map(parent => [parent.personID, parent]));
                const commonParents = parentsA.filter(parent => parentsBMap.has(parent.personID));
                let sideParents = null;
                if (lcaIds.length === 1 && commonParents.length === 1) {
                    const sharedId = commonParents[0].personID;
                    const otherParentA = parentsA.find(parent => parent.personID !== sharedId) || null;
                    const otherParentB = parentsB.find(parent => parent.personID !== sharedId) || null;
                    if (otherParentA || otherParentB) {
                        sideParents = {
                            left: otherParentA,
                            right: otherParentB,
                        };
                    }
                }

                this.graphData = {
                    mode: 'blood',
                    lcaIds,
                    lcaNodes,
                    branchLeft,
                    branchRight,
                    sideParents,
                    spouseLabelByPair,
                    lcaPairByNodeId,
                    lcaPairByTransition,
                };
            } catch (err) {
                console.error('Fout bij ophalen grafiekdata:', err);
                this.graphError = 'Kon de verwantschapsgrafiek niet opbouwen.';
            } finally {
                this.graphLoading = false;
                this.$nextTick(() => {
                    this.updateRelGridFitScale();
                });
            }
        },
        async loadData(personID1Arg, personID2Arg) {
            this.loading = true;
            const personID1 = personID1Arg || this.selectedPerson1Id;
            const personID2 = personID2Arg || this.selectedPerson2Id;

            if (!personID1 || !personID2) {
                this.loading = false;
                this.resetRelationshipState();
                return;
            }

            this.selectedPerson1Id = personID1;
            this.selectedPerson2Id = personID2;
            this.relationshipFound = null;
            this.error = null;

            try {
                const [p1Data, p2Data] = await Promise.all([
                    getPersonById(personID1),
                    getPersonById(personID2)
                ]);
                this.person1 = p1Data;
                this.person2 = p2Data;
                this.selectedPerson1Label = this.buildPersonLabel(p1Data);
                this.selectedPerson2Label = this.buildPersonLabel(p2Data);
                this.personSearchQuery1 = this.selectedPerson1Label;
                this.personSearchQuery2 = this.selectedPerson2Label;

                const data = await getRelationship(personID1, personID2, this.maxRelationshipDepth);
                if (data.relationshipFound) {
                    this.relationshipFound = true;
                    this.allPaths = data.paths || [data.path];
                    this.activePathIndex = 0;
                    this.relationshipSummary = data.summary;
                } else {
                    this.relationshipFound = false;
                    this.relationshipSummary = data.message || 'Geen relatie gevonden.';
                }

            } catch (error) {
                console.error('Fout bij ophalen relatiegegevens:', error);
                this.error = 'Er is een fout opgetreden bij het ophalen van de relatiegegevens. Probeer opnieuw of kies een andere combinatie.';
            } finally {
                this.loading = false;
            }

            if (personID1 && personID2) {
                await this.loadGraphData(personID1, personID2);
            }
        },
    },
    async created() {
        await this.refreshDnaTesters();
        await this.initializeFromContext();
    },
    mounted() {
        document.body.classList.add('relationship-tree-body-bg');
        window.addEventListener('resize', this.handleRelGridResize);
        document.addEventListener('click', this.handleClickOutsidePicker);
        this.handleRelGridResize();
    },
    beforeUnmount() {
        document.body.classList.remove('relationship-tree-body-bg');
        window.removeEventListener('resize', this.handleRelGridResize);
        document.removeEventListener('click', this.handleClickOutsidePicker);
        clearTimeout(this.searchDebounceTimer1);
        clearTimeout(this.searchDebounceTimer2);
    }
};
</script>

<style scoped>
:global(body.relationship-tree-body-bg) {
    background: url('@/assets/noise128x128.png'), var(--brand-bg-4);
    background-repeat: repeat;
    background-size: auto;
}

/* Achtergrond en container */
.RelationshipTree_wrapper {
    width: 100%;
    height: 100%;
    min-height: 100vh;
    position: relative;
}

.wrapper {
    max-width: 100%;
    padding: 96px 20px;
    margin: 0 auto;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 48px;
}

/* Override spinner color to yellow on this page */
:global(.RelationshipTree_wrapper .path) {
    stroke: var(--brand-color-3);
}

/* Header styling */
h2 {
    color: #fff;
    z-index: 1;
    /* position: absolute; */
    left: 0;
    top: 24px;
    width: 100%;
    text-align: center;
}

h2 a {
    color: var(--brand-color-3);
    display: inline;
    font-size: 36px;
    font-weight: 700;
    line-height: 125%;
}

h2 a:hover {
    text-decoration: underline;
}

.relationship-subtitle {
    text-align: center;
    color: var(--brand-text-light);
    margin: -28px auto 0;
    max-width: 760px;
}

.relationship-title {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
}

.inline-person-editor {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.inline-person-trigger {
    border: 0;
    background: transparent;
    color: var(--brand-color-3);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 36px;
    font-weight: 700;
    line-height: 1.25;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color 0.2s ease;
}

.inline-person-trigger:hover {
    text-decoration-color: var(--brand-color-3);
}

.inline-person-caret {
    font-size: 0.7em;
    opacity: 0.9;
    transform: translateY(1px);
}

.inline-editor-popover {
    position: relative;
    display: flex;
    align-items: center;
}

.inline-editor-popover input {
    width: min(360px, 72vw);
    height: 48px;
    padding: 0 14px;
    font-size: 18px;
    color: var(--brand-text-light);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 10px;
    outline: none;
}

.inline-editor-popover input::placeholder {
    color: rgba(255, 255, 255, 0.66);
}

.search-suggestions {
    position: absolute;
    background-color: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    list-style: none;
    /* margin: 6px 0 0; */
    padding: 16px 0;
    border-radius: 16px;
    display: flex;
    gap: 0;
    flex-direction: column;
    align-items: flex-start;
    max-height: calc(100vh - 166px - 24px);
    overflow-y: auto;
    left: 0;
    right: auto;
    min-width: 100%;
    z-index: 9999;
}

.search-suggestions li {
    padding: 12px 24px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
    border-radius: 0;
    color: #1D1D1D;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
}

.search-suggestions li:hover {
    background-color: #f0f0f0;
}

.search-suggestions span {
    color: #1D1D1D;
    line-height: 100%;
}

.search-suggestions span:nth-of-type(1) {
    font-weight: 600;
    font-size: 16px;
}

.search-suggestions span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

.locked_name {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.locked_name img {
    width: 12px;
    height: 12px;
}

.locked_name:hover {
    text-decoration: underline;
}

.picker-hint {
    margin: -32px auto 0;
    color: var(--brand-text-light);
    font-size: 13px;
    text-align: center;
}

.picker-hint--error {
    color: #ffb5b5;
}

.empty-state {
    text-align: center;
    color: var(--brand-text-light);
    opacity: 0.9;
    margin: 24px auto 0;
}

/* Loader styling */
.loader {
    display: flex;
    justify-content: center;
    align-items: center;
}

.spinner {
    width: 50px;
    height: 50px;
}

/* Relationship chain layout */
.relationship-chain {
    /* margin-top: 120px; */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 48px;
    margin: auto;
    width: 100%;
    /* max-width: 960px; */
}

.relationship-description {
    /* margin-top: 16px; */
    text-align: center;
    font-weight: 400;
    font-size: 16px;
    color: var(--brand-text-light);
    padding: 24px;
    border-radius: var(--border-radius-md);
    border: 2px dashed var(--brand-color-3);
    display: none;
}

.relationship-description span {
    color: var(--brand-color-3);
    font-weight: bold;
}

.graph-loading {
    text-align: center;
    color: var(--brand-text-light);
    font-size: 14px;
}

.relationship-graph {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
}

.graph-affinal-note {
    text-align: center;
    color: var(--brand-text-light);
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    display: none;
}

/* Tabbladen voor alternatieve relatiepaden */
.path-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
}

.path-tab {
    padding: 8px 18px;
    border-radius: 20px;
    border: 2px solid rgba(255, 255, 255, 0.25);
    background: transparent;
    color: var(--brand-text-light);
    font-size: 13px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.path-tab:hover {
    border-color: var(--brand-color-3);
    color: var(--brand-color-3);
}

.path-tab--active {
    border-color: var(--brand-color-3);
    background: rgba(var(--brand-color-3-rgb, 255, 190, 0), 0.12);
    color: var(--brand-color-3);
    font-weight: 600;
}

.graph-legend {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 16px;
    color: var(--brand-text-light);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    display: none;
}

.legend-item--left {
    text-align: left;
}

.legend-item--center {
    text-align: center;
}

.legend-item--right {
    text-align: right;
}

.relationship-graph ::v-deep(.PersonCard_name) {
    width: 100%;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.relationship-graph ::v-deep(.PersonCard_years) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.relationship-graph ::v-deep(.PersonCard_text_wrapper) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    box-sizing: border-box;
}

/* Override PersonCard width to match the fixed column width */
.rel-grid ::v-deep(.PersonCard--small) {
    width: 326px;
    z-index: 2;
}

/* ──────────────────────────────────────────────
   Spacer label columns
────────────────────────────────────────────── */
.rel-grid-spacer {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
}

.rel-grid-spacer-label {
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--brand-bg-4);
    color: var(--brand-text-light);
    font-size: 12px;
    letter-spacing: 0.06em;
    /* text-transform: uppercase; */
    max-width: 144px;
    white-space: normal;
    overflow-wrap: break-word;
    text-align: center;
    position: relative;
    z-index: 1;
    line-height: 100%;
}

/* ──────────────────────────────────────────────
   Dynamic relationship grid
────────────────────────────────────────────── */
.rel-grid-scroll {
    /* overflow-x: auto; */
    /* overflow-y: visible; */
    padding: 8px 0 24px;
}

.rel-grid-fit {
    position: relative;
}

.rel-grid-container {
    /* width / height set by inline style */
    position: relative;
}

.rel-grid-svg {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 0;
    overflow: visible;
}

.rel-grid {
    /* template columns/rows and gaps set by inline style */
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
}

.rel-grid-cell {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* LCA couple: two cards side by side */
.rel-lca-pair {
    display: flex;
    gap: 40px;
    align-items: center;
    justify-content: center;
}

.relationship-summary {
    font-size: 16px;
    color: var(--brand-text-light);
}

/* Error message */
.error-message {
    color: var(--brand-text-light);
    width: 100%;
    text-align: center;
    margin: 16px 0;
}

.rel-grid-download-btn {
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    z-index: 3;
    border: 1px solid var(--brand-color-1);
    background: var(--brand-color-1);
    color: #fff;
    padding: 12px 18px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.rel-grid-download-btn i {
    font-size: 16px;
}

.rel-grid-download-btn:hover:not(:disabled) {
    /* background: rgba(var(--brand-color-1-rgb, 193, 39, 45), 0.2); */
    /* border-color: var(--brand-color-1); */
    transform: translateX(-50%) translateY(-4px);
}

.rel-grid-download-btn:disabled {
    opacity: 0.7;
    cursor: progress;
}

.common-ancestors {
    position: relative;
}

.common-ancestors::after {
    content: '';
    position: absolute;
    bottom: -24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.24);
    height: 24px;
    width: 2px;
    z-index: 0;
}

.common-ancestors .branch-list {
    display: flex;
    flex-direction: column;
    max-width: 480px;
    margin: 0 auto;
}

.common-ancestors .branch-list p {
    color: var(--brand-text-light);
    width: 100%;
    text-align: center;
    margin: 1px auto;
    font-size: 12px;
}

.error-message {
    color: var(--brand-text-light);
    width: 100%;
    text-align: center;
    margin: 0 auto;
}

.branches {
    display: flex;
    flex-direction: row;
    gap: 24px;
    padding-top: 48px;
    position: relative;
}

.branches::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.24);
    height: 2px;
    width: calc(50% + 12px);
    z-index: 0;
}

.single-branch-container {
    padding-top: 24px;
    max-width: 480px;
    margin: 0 auto;
}

.single-branch-container::before {
    display: none;
}

.branch {
    flex: 1 0 0;
    display: flex;
}

.branches .branch-list {
    display: flex;
    flex-direction: column;
    flex: 1 0 0;
    gap: 24px;
}

.branch-item {
    width: 100%;
}

.branches .branch-item {
    position: relative;
}

.branches .branch-item::before {
    content: '';
    position: absolute;
    top: -24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.24);
    height: 24px;
    width: 2px;
    z-index: 0;
}

.single-branch {
    margin: 0 auto;
}

.path {
    stroke: var(--brand-color-3)
}

@media (max-width: 600px) {
    .wrapper {
        padding: 56px 16px 72px;
    }

    .inline-person-trigger {
        font-size: 24px;
    }

    .relationship-title {
        gap: 6px;
    }

    .inline-editor-popover input {
        width: min(300px, 86vw);
        height: 42px;
        font-size: 16px;
    }

    .picker-hint {
        margin-top: -40px;
    }

    .rel-grid-download-btn {
        bottom: 16px;
        width: calc(100% - 32px);
        max-width: 320px;
    }

    .relationship-chain {
        width: 100%;
    }
}
</style>
